import {
  DraftPointFeature,
  getDraftPointFeature,
} from "common/utils/mapEditor";
import {
  insertPointOnNearestEdge,
  moveDraftPoint,
} from "portal/utils/boundaryEdit";
import {
  isPivotArmTipFeature,
  LngLat,
  useBoundaryDraftLayers,
} from "./BoundaryDraftLayer";
import { MapExtension } from "../../FarmMap";
import {
  PendingZone,
  setBoundaryEditActivePoint,
  setBoundaryEditCarrying,
  setBoundaryEditPoints,
  setPointMoveCarrying,
  setPointMovePosition,
  updatePendingZone,
} from "portal/state/farmExplorer";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import bearing from "@turf/bearing";
import distance from "@turf/distance";
import React, { useRef, useState } from "react";

type PivotArm = Required<
  Pick<PendingZone, "armBearingDegrees" | "armLengthMeters">
>;

export interface BoundaryEditor extends MapExtension {
  active: boolean;
  /** a ring is being edited, so stored zones and points should not react */
  editingRing: boolean;
  /** the stored points the base layers may still draw; the draft draws the rest */
  visibleStoredPoints: (pointIds: string[]) => string[];
}

export const useBoundaryEditor = (): BoundaryEditor => {
  const dispatch = useDispatch();
  const { boundaryEdit, pointMove } = useFarmExplorer();

  const isEditing = Boolean(boundaryEdit || pointMove);
  const carriedPointId = boundaryEdit?.carrying
    ? boundaryEdit.activePointId
    : pointMove?.carrying
    ? pointMove.pointId
    : undefined;
  // a click on empty map places the carried point or inserts one
  const mapClickPlaces =
    carriedPointId !== undefined ||
    Boolean(boundaryEdit && !boundaryEdit.activePointId);

  // a drag is local until release; dispatching per frame lagged the pointer
  const dragPointIdRef = useRef<string>();
  const dragPosRef = useRef<LngLat>();
  const dragFrameRef = useRef<number>();
  const [dragging, setDragging] = useState<{
    pointId: string;
    position: LngLat;
  }>();
  const dragDraftPoint = (pointId: string, lng: number, lat: number): void => {
    if (pointMove) {
      dispatch(setPointMovePosition({ lng, lat }));
      return;
    }
    if (!boundaryEdit) {
      return;
    }
    dispatch(
      setBoundaryEditPoints(
        moveDraftPoint(boundaryEdit.points, pointId, lng, lat)
      )
    );
  };

  // a new pivot's arm reaches to a click after its center, or to its dragged tip
  const pivotCenter =
    boundaryEdit?.pending?.kind === "centerPivot"
      ? boundaryEdit.points[0]
      : undefined;
  const armTo = ({ lng, lat }: LngLat): PivotArm | undefined => {
    if (!pivotCenter) {
      return;
    }
    const center = [pivotCenter.lng, pivotCenter.lat];
    return {
      armBearingDegrees: bearing(center, [lng, lat]),
      // to the tenth of a meter the length input shows
      armLengthMeters:
        Math.round(distance(center, [lng, lat], { units: "meters" }) * 10) / 10,
    };
  };
  const armDragPosRef = useRef<LngLat>();
  const armDragFrameRef = useRef<number>();
  const [armDrag, setArmDrag] = useState<PivotArm>();

  // pointer position over the map, refreshed at most once a frame
  const [cursorPos, setCursorPos] = useState<LngLat>();
  const cursorPosRef = useRef<LngLat>();
  const cursorFrameRef = useRef<number>();
  const clearCursor = (): void => {
    if (cursorFrameRef.current !== undefined) {
      cancelAnimationFrame(cursorFrameRef.current);
      cursorFrameRef.current = undefined;
    }
    cursorPosRef.current = undefined;
    setCursorPos(undefined);
  };

  const placeAt = ({ lng, lat }: LngLat): void => {
    if (pointMove) {
      if (pointMove.carrying) {
        dispatch(setPointMovePosition({ lng, lat }));
      }
      return;
    }
    if (!boundaryEdit) {
      return;
    }
    if (!boundaryEdit.activePointId) {
      const arm = armTo({ lng, lat });
      if (arm) {
        dispatch(updatePendingZone(arm));
        return;
      }
      dispatch(
        setBoundaryEditPoints(
          insertPointOnNearestEdge(boundaryEdit.points, lng, lat)
        )
      );
      return;
    }
    if (boundaryEdit.carrying) {
      dispatch(
        setBoundaryEditPoints(
          moveDraftPoint(
            boundaryEdit.points,
            boundaryEdit.activePointId,
            lng,
            lat
          )
        )
      );
      dispatch(setBoundaryEditCarrying(false));
    }
  };

  const {
    Layers: BoundaryDraftLayers,
    interactionLayers: draftInteractionLayers,
    idsToLoad: draftIdsToLoad,
  } = useBoundaryDraftLayers({
    pointCursor: carriedPointId || dragging ? "grabbing" : "grab",
    ghostCursor: carriedPointId ? "grabbing" : "copy",
    onPointClick: (f: DraftPointFeature, position?: LngLat) => {
      const { pointId } = f.properties;
      if (pointId === carriedPointId) {
        if (position) {
          placeAt(position);
        }
        return;
      }
      if (boundaryEdit) {
        dispatch(setBoundaryEditActivePoint(pointId));
      } else if (pointMove) {
        dispatch(setPointMoveCarrying(true));
      }
    },
    onPointDragStart: (f: DraftPointFeature) => {
      dragPointIdRef.current = f.properties.pointId;
      // drawn at the dragged position, not the cursor
      clearCursor();
    },
    onPointDrag: (position: LngLat) => {
      dragPosRef.current = position;
      if (dragFrameRef.current !== undefined) {
        return;
      }
      dragFrameRef.current = requestAnimationFrame(() => {
        dragFrameRef.current = undefined;
        if (dragPointIdRef.current && dragPosRef.current) {
          setDragging({
            pointId: dragPointIdRef.current,
            position: dragPosRef.current,
          });
        }
      });
    },
    onPointDragEnd: ({ lng, lat }: LngLat) => {
      if (dragFrameRef.current !== undefined) {
        cancelAnimationFrame(dragFrameRef.current);
        dragFrameRef.current = undefined;
      }
      const pointId = dragPointIdRef.current;
      dragPointIdRef.current = undefined;
      dragPosRef.current = undefined;
      setDragging(undefined);
      if (pointId) {
        dragDraftPoint(pointId, lng, lat);
      }
      // a drag puts a carried point down
      if (boundaryEdit?.carrying) {
        dispatch(setBoundaryEditCarrying(false));
      }
    },
    onGhostClick: placeAt,
    onPivotArmDrag: pivotCenter
      ? (position: LngLat) => {
          clearCursor();
          armDragPosRef.current = position;
          if (armDragFrameRef.current !== undefined) {
            return;
          }
          armDragFrameRef.current = requestAnimationFrame(() => {
            armDragFrameRef.current = undefined;
            if (armDragPosRef.current) {
              setArmDrag(armTo(armDragPosRef.current));
            }
          });
        }
      : undefined,
    onPivotArmDragEnd: (position: LngLat) => {
      if (armDragFrameRef.current !== undefined) {
        cancelAnimationFrame(armDragFrameRef.current);
        armDragFrameRef.current = undefined;
      }
      armDragPosRef.current = undefined;
      setArmDrag(undefined);
      const arm = armTo(position);
      if (arm) {
        dispatch(updatePendingZone(arm));
      }
    },
  });

  const layers = boundaryEdit ? (
    <BoundaryDraftLayers
      points={boundaryEdit.points}
      activePointId={boundaryEdit.activePointId}
      cursor={dragging?.position ?? (mapClickPlaces ? cursorPos : undefined)}
      carriedPointId={dragging?.pointId ?? carriedPointId}
      showInsertPreview={!boundaryEdit.activePointId}
      pivotArmLengthMeters={
        boundaryEdit.pending?.kind === "centerPivot"
          ? armDrag?.armLengthMeters ?? boundaryEdit.pending.armLengthMeters
          : undefined
      }
      pivotArmBearingDegrees={
        armDrag?.armBearingDegrees ?? boundaryEdit.pending?.armBearingDegrees
      }
    />
  ) : (
    pointMove && (
      <BoundaryDraftLayers
        points={[pointMove]}
        activePointId={pointMove.pointId}
        cursor={dragging?.position ?? (mapClickPlaces ? cursorPos : undefined)}
        carriedPointId={dragging?.pointId ?? carriedPointId}
      />
    )
  );

  return {
    active: isEditing,
    editingRing: Boolean(boundaryEdit),
    layers,
    interactionLayers: isEditing ? draftInteractionLayers : [],
    idsToLoad: draftIdsToLoad,
    mapProps: {
      onMapMouseMove: (feature, lng, lat) => {
        if (!mapClickPlaces) {
          return;
        }
        // no ghost over a stored point; the carried point is drawn under the cursor
        const draftPoint = getDraftPointFeature(feature);
        if (
          isPivotArmTipFeature(feature) ||
          (draftPoint && draftPoint.properties.pointId !== carriedPointId)
        ) {
          clearCursor();
          return;
        }
        cursorPosRef.current = { lng, lat };
        if (cursorFrameRef.current !== undefined) {
          return;
        }
        cursorFrameRef.current = requestAnimationFrame(() => {
          cursorFrameRef.current = undefined;
          setCursorPos(cursorPosRef.current);
        });
      },
      onMapMouseLeave: clearCursor,
      defaultCursor:
        carriedPointId !== undefined
          ? "grabbing"
          : boundaryEdit && !boundaryEdit.activePointId
          ? "copy"
          : undefined,
    },
    visibleStoredPoints: (pointIds) =>
      boundaryEdit
        ? []
        : pointMove
        ? pointIds.filter((id) => id !== pointMove.pointId)
        : pointIds,
  };
};
